import { Add, Edit, Save, Search, Close } from '@mui/icons-material'
import { AppBar, Box, Button, IconButton, Paper, Table, TableBody, TableCell, TableContainer, TableHead, TableRow, TextField, Toolbar, Tooltip, Typography, InputBase } from '@mui/material'
import React, { useEffect, useState, useRef, useCallback } from 'react'
import { useDispatch, useSelector } from 'react-redux';
import { Link, useNavigate } from "react-router-dom";
import { apiV2 } from '../../../actions/index';
import { fetchAllDailyReflections, updateDailyInspirationDescription } from '../../../actions/dailyReflectionActions';
import { format } from 'date-fns';
import Swal from 'sweetalert2';
import LoadingBackdrop from '../../Common/LoadingBackdrop';
import MainContentContainer from '../../MainContentContainer/MainContentContainer';

export default function DailyReflectionPage() {
  const dispatch = useDispatch();
  const navigate = useNavigate();

  const currentDate = format(new Date(), 'yyyy-MM-dd');

  const [dailyReflections, setDailyReflections] = useState(null);
  const [dailyReflectionDescription, setDailyReflectionDescription] = useState(null);
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchText, setSearchText] = useState("");
  const searchInputRef = useRef(null);

  const loading = useSelector(state => state.daily_reflections.loading);
  const drStore = useSelector(state => state.daily_reflections.daily_reflections);

  const handleSearchChange = (e) => {
    setSearchText(e.target.value);
  };

  const handleToggleSearch = useCallback(() => {
    setIsSearchOpen((prev) => !prev);
    if (isSearchOpen) {
      setSearchText("");
    } else {
      setTimeout(() => {
        searchInputRef.current?.focus();
      }, 0);
    }
  }, [isSearchOpen]);

  useEffect(() => {
    const handleKeyDown = (e) => {
      if ((e.metaKey || e.ctrlKey) && e.key === 'f' && e.shiftKey) {
        e.preventDefault();
        if (!isSearchOpen) {
          handleToggleSearch();
        }
      }
      if (e.key === 'Escape' && isSearchOpen) {
        e.preventDefault();
        handleToggleSearch();
      }
    };

    window.addEventListener('keydown', handleKeyDown);
    return () => {
      window.removeEventListener('keydown', handleKeyDown);
    };
  }, [isSearchOpen, handleToggleSearch]);

  useEffect(() => {
    if (drStore) {
      setDailyReflections(drStore);
    } else {
      dispatch(fetchAllDailyReflections());
    }
  }, [drStore]);

  useEffect(() => {
    apiV2.get('/daily-inspirations/description')
      .then(response => {
        setDailyReflectionDescription(response.data.general_info_value || '');
      })
      .catch(() => {});
  }, []);

  const handleSaveDescription = () => {
    dispatch(updateDailyInspirationDescription(dailyReflectionDescription));
  }

  const handleEdit = dailyReflection => {
    navigate(`/daily-inspirations/${dailyReflection.daily_reflection_id}/edit`);
  }



  return (
    <MainContentContainer>
      <LoadingBackdrop open={loading || dailyReflections == null} />
      <AppBar position="sticky">
        <Toolbar>
          {isSearchOpen ? (
            <Box display="flex" alignItems="center" flexGrow={1}>
              <InputBase
                inputRef={searchInputRef}
                value={searchText}
                onChange={handleSearchChange}
                sx={{ flexGrow: 1, px: 1, border: "1px solid #ccc", borderRadius: 1, backgroundColor: "white" }}
                placeholder="Search..."
              />
              <IconButton color="inherit" onClick={handleToggleSearch}>
                <Close />
              </IconButton>
            </Box>
          ) : (
            <Typography variant="h6" component="div" sx={{ flexGrow: 1 }}>
              Daily Inspiration
            </Typography>
          )}
          <IconButton color="inherit" onClick={handleToggleSearch}>
            <Search />
          </IconButton>
          <Button color="inherit" component={Link} to={`/daily-inspirations/create`} startIcon={<Add />}>
            Add New
          </Button>
        </Toolbar>
      </AppBar>
      <Box sx={{ mt: 3, mb: 2 }}>
        <Paper sx={{ p: 2 }}>
          <Box sx={{ display: 'flex', gap: 2, alignItems: 'center' }}>
            <TextField
              value={dailyReflectionDescription || ''}
              onChange={e => setDailyReflectionDescription(e.target.value)}
              multiline
              rows={2}
              fullWidth
              variant="outlined"
              label="Description"
              placeholder="Enter daily inspiration description..."
            />
            <Button
              variant="contained"
              onClick={handleSaveDescription}
              startIcon={<Save />}
              color="success"
            >
              Update
            </Button>
          </Box>
        </Paper>
      </Box>
      <TableContainer component={Paper} sx={{ marginTop: 3 }}>
        <Table aria-label="daily inspiration table" size="small">
          <TableHead>
            <TableRow sx={{ backgroundColor: '#f5f5f5' }}>
              <TableCell><strong>Id</strong></TableCell>
              <TableCell><strong>Scheduled For</strong></TableCell>
              <TableCell><strong>Title</strong></TableCell>
              <TableCell align="right"><strong>Actions</strong></TableCell>
            </TableRow>
          </TableHead>
          <TableBody>
            {
              dailyReflections && dailyReflections.length > 0 ? (
                dailyReflections
                  .filter(dailyReflection =>
                    searchText ?
                      dailyReflection.title.toLowerCase().includes(searchText.toLowerCase()) ||
                      dailyReflection.daily_reflection_id.toString().includes(searchText)
                      : true
                  )
                  .map(dailyReflection => {
                    const dailyReflectionDate = dailyReflection.date_epoch ? format(new Date(dailyReflection.date_epoch * 1000), 'yyyy-MM-dd') : "N/A";
                    const isCurrentDateProps = currentDate === dailyReflectionDate ? { backgroundColor: 'cornsilk' } : null;

                    return (
                      <TableRow key={dailyReflection.daily_reflection_id} sx={isCurrentDateProps}>
                        <TableCell>
                          <Typography variant="subtitle1">{dailyReflection.daily_reflection_id}</Typography>
                        </TableCell>
                        <TableCell>
                          <Typography variant="subtitle1">{dailyReflectionDate}</Typography>
                        </TableCell>
                        <TableCell>
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <Typography variant="subtitle1">{dailyReflection.title || ''}</Typography>
                          </Box>
                        </TableCell>
                        <TableCell align="right">
                          <Tooltip title="Edit" placement="top">
                            <IconButton size="small" color="info" onClick={() => handleEdit(dailyReflection)}>
                              <Edit />
                            </IconButton>
                          </Tooltip>
                        </TableCell>
                      </TableRow>
                    );
                  })
              ) : null
            }
          </TableBody>
        </Table>
      </TableContainer>
    </MainContentContainer>
  )
}
